import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGlobe, FaChevronDown, FaCheck } from 'react-icons/fa';
import { useI18n } from '../contexts/I18nContext';

const LanguageSwitcher = ({ variant = 'default', showInOnboarding = false }) => {
  const { language, setLanguage, supportedLanguages, getLanguageName } = useI18n();
  const [isOpen, setIsOpen] = useState(false);

  // Only show en/hi in onboarding
  const availableLanguages = showInOnboarding
    ? supportedLanguages.filter(lang => ['en', 'hi'].includes(lang))
    : supportedLanguages;

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setIsOpen(false);
  };

  if (variant === 'minimal') {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 dark:text-slate-200 dark:hover:text-white transition-colors"
        >
          <FaGlobe className="text-base" />
          <span className="uppercase">{language}</span>
          <FaChevronDown className={`text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {isOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsOpen(false)}
              />
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute top-full right-0 mt-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg z-50 min-w-[150px]"
              >
                {availableLanguages.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => handleLanguageChange(lang)}
                    className={`w-full px-4 py-2 text-left text-sm text-gray-900 dark:text-slate-100 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-between ${
                      language === lang ? 'bg-gray-50 dark:bg-slate-700' : ''
                    }`}
                  >
                    <span>{getLanguageName(lang)}</span>
                    {language === lang && <FaCheck className="text-blue-600 dark:text-blue-400" />}
                  </button>
                ))}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
      >
        <FaGlobe className="text-gray-600" />
        <span className="text-sm font-medium text-gray-700">{getLanguageName(language)}</span>
        <FaChevronDown className={`text-xs text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[180px] overflow-hidden"
            >
              <div className="py-1">
                {availableLanguages.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => handleLanguageChange(lang)}
                    className={`w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 transition-colors flex items-center justify-between ${
                      language === lang ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{getLanguageName(lang)}</span>
                      <span className="text-xs text-gray-500 uppercase">({lang})</span>
                    </div>
                    {language === lang && <FaCheck className="text-blue-600" />}
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;

